import axios from 'axios'
import {getToken} from '@/lib/token'
import {getSelectedOrganizationId} from '@/lib/organization-selection';
import {useAuthStore} from '@/stores/auth.ts';
import {useOrganizationStore} from '@/stores/organization';
import router from '@/router';
import {redirectTarget} from '@/lib/redirect';

export const ORGANIZATION_HEADER = 'X-Organization-Id';

// The backend rejects every request with an unknown organization header, so account-level
// calls go without it; organization management addresses the organization in the path.
const ORGANIZATION_INDEPENDENT = /^\/?(auth|user|organizations|oauth|mcp)(\/|$)/;

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Sent without the selected organization, whose refusal must then not re-scope the page. */
    withoutOrganization?: boolean;
  }
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
})

// Inject Authorization header if token exists
api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers = config.headers ?? {}
    config.headers['Authorization'] = `Bearer ${token}`
  }
  const organizationId = getSelectedOrganizationId()
  if (
    token &&
    organizationId &&
    !config.withoutOrganization &&
    !ORGANIZATION_INDEPENDENT.test(config.url ?? '')
  ) {
    config.headers = config.headers ?? {}
    config.headers[ORGANIZATION_HEADER] = organizationId
  }
  return config
})

// An expired session on the consent or handoff page must return there after sign-in, or the app's request is lost.
const RETURN_AFTER_LOGIN = ['OAuthConsent', 'McpHandoff'];

function loginRoute() {
  const current = router.currentRoute.value;
  const redirect = RETURN_AFTER_LOGIN.includes(current.name as string)
    ? redirectTarget({ redirect: current.fullPath })
    : null;
  return redirect ? { name: 'Login', query: { redirect } } : { name: 'Login' };
}

function fromCurrentSession(authorization: unknown): boolean {
  const token = getToken()
  return (authorization ?? null) === (token ? `Bearer ${token}` : null)
}

// Organization management addresses the organization in the path instead of the header.
function addressesSelectedOrganization(url: string | undefined): boolean {
  const match = /^\/?organizations\/([^/?]+)/.exec(url ?? '')
  const selected = getSelectedOrganizationId()
  return Boolean(match && selected && decodeURIComponent(match[1]!) === selected)
}

/**
 * Re-reads the memberships. When the selection had to fall back to the default organization,
 * starts a forced navigation so pages and guards waiting for the old organization are replaced.
 */
export async function refreshOrganizations() {
  try {
    const switched = await useOrganizationStore().load()
    if (switched) void router.replace({ name: 'Dashboard', force: true })
  } catch {
    // Keep the selection; callers report their own errors and the switcher offers a retry.
  }
}

// Optional: handle 401 globally (e.g., clear token and redirect to login)
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    // A response to a request of an earlier session must not log out or re-scope the current one.
    if (!fromCurrentSession(error?.config?.headers?.['Authorization'])) {
      return Promise.reject(error)
    }
    const status = error?.response?.status
    const scopedToOrganization = Boolean(error?.config?.headers?.[ORGANIZATION_HEADER])
    const organizationIndependent = Boolean(error?.config?.withoutOrganization)
    if (
      status === 404 &&
      error?.response?.data?.code === 'ORGANIZATION_NOT_FOUND' &&
      (scopedToOrganization || addressesSelectedOrganization(error?.config?.url))
    ) {
      await refreshOrganizations()
    } else if (
      !organizationIndependent &&
      status === 403 &&
      error?.response?.data?.code === 'ORGANIZATION_PERMISSION_DENIED'
    ) {
      // The role may have changed since the organizations were loaded.
      void refreshOrganizations()
    }
    if (error?.response?.status === 401) {
      // Get the URL of the failed request
      const requestUrl = error.config?.url || '';

      // Define endpoints where a 401 is expected and should NOT cause a logout
      const ignoreList = [
        'auth/token',
        'auth/change-password',
        'auth/mfa',
        'auth/login',
        'auth/forgot-password',
        'auth/reset-password',
      ];

      // Check if the failed request's URL is in our ignore list
      const isIgnored = ignoreList.some(path => requestUrl.endsWith(path));

      if (!isIgnored) {
        // This is a TRUE "session expired" 401
        // It came from an endpoint NOT on the ignore list (e.g., /api/servers)

        const authStore = useAuthStore();
        authStore.logout();

        router.push(loginRoute());
      }

      // If the URL *is* on the ignore list, we do nothing.
      // We let the error pass through to be handled by the
      // .catch() block in the auth store (e.g., to show "Wrong Password").
    }
    return Promise.reject(error)
  },
)

export default api
